import { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  Typography,
  Box,
} from '@mui/material'

const OPTIONS = [
  { value: 'copy', title: 'Copy', desc: 'Leaves your bowtie here and makes a copy in your chosen destination.' },
  { value: 'move', title: 'Move', desc: "Moves your bowtie from this workspace to your chosen destination." },
]

// Lets the user add a personal bowtie to an organisation — either as a copy
// (original kept) or a one-way move.
function MoveOrCopyToOrgDialog({ open, bowtie, orgs, busy, onClose, onConfirm }) {
  const [orgSlug, setOrgSlug] = useState('')
  const [action, setAction] = useState('copy')

  useEffect(() => {
    if (open) {
      setOrgSlug(orgs.length === 1 ? orgs[0].slug : '')
      setAction('copy')
    }
  }, [open, orgs])

  const targetOrg = orgs.find((o) => o.slug === orgSlug)

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Add to an organisation</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          "{bowtie?.title}"
        </Typography>

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel id="mc-org-label">Organisation</InputLabel>
          <Select
            labelId="mc-org-label"
            label="Organisation"
            value={orgSlug}
            onChange={(e) => setOrgSlug(e.target.value)}
          >
            {orgs.map((o) => (
              <MenuItem key={o._id} value={o.slug}>
                {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {OPTIONS.map((opt) => {
            const selected = action === opt.value
            return (
              <Box
                key={opt.value}
                role="radio"
                aria-checked={selected}
                tabIndex={0}
                onClick={() => setAction(opt.value)}
                onKeyDown={(e) => {
                  if (e.key === ' ' || e.key === 'Enter') {
                    e.preventDefault()
                    setAction(opt.value)
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                  p: 1.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selected ? '#E67E22' : 'divider',
                  backgroundColor: selected ? 'rgba(230, 126, 34, 0.08)' : 'grey.50',
                  transition: 'border-color 0.15s, background-color 0.15s',
                  '&:hover': { borderColor: selected ? '#E67E22' : 'text.disabled' },
                }}
              >
                <Radio
                  checked={selected}
                  size="small"
                  tabIndex={-1}
                  sx={{ p: 0, mt: '2px', '&.Mui-checked': { color: '#E67E22' } }}
                />
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                    {opt.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {opt.desc}
                  </Typography>
                </Box>
              </Box>
            )
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!orgSlug || busy}
          onClick={() => onConfirm({ action, slug: orgSlug, orgName: targetOrg?.name })}
          sx={{
            textTransform: 'none',
            backgroundColor: '#E67E22',
            '&:hover': { backgroundColor: '#D35400' },
          }}
        >
          {busy ? 'Working…' : action === 'copy' ? 'Copy to organisation' : 'Move to organisation'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default MoveOrCopyToOrgDialog
